<template>
  <ValidationObserver
    ref="modalObserver"
    v-slot="{ valid }"
  >
    <v-dialog
      v-model="visible"
      scrollable
      :fullscreen="fullscreen"
      :persistent="true"
      :width="width"
      :height="800"
      :noClickAnimation="true"
    >
      <form
        class="modal"
        :data-cy="dataCy.modals.modalForm"
      >
        <v-card>
          <v-card-title
            class="m-0"
          >
            <v-row
              class="w-100"
            >
              <v-col
                class="titleStyle"
              >
                <span>{{ title }}</span>
                <span v-if="showBreadcrumbs || meterName">:</span>
                <span
                  v-if="meterName"
                  class="breadCrumbStyle"
                >
                  {{ meterName }}
                </span>
                <span
                  v-if="showBreadcrumbs"
                  class="breadCrumbStyle"
                >
                  <v-breadcrumbs
                    :items="modalBreadcrumbs"
                    :divider="dividerSelect"
                    large
                  />
                </span>
              </v-col>
              <v-col
                class="p-0 w-25 close"
              >
                <v-btn
                  text
                  class="capitalize large close-button"
                  data-cy="buttonClose"
                  @click="hide"
                >
                  {{ $t('forms.labels.close') }}<v-icon>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="showLangs"
                xs="12"
                sm="12"
                md="3"
                lg="3"
                xl="3"
              >
                <VSelect
                  v-if="showLangs"
                  v-model="lang"
                  :label="$t('forms.labels.lang')"
                  itemValue="key"
                  :items="displayedLangs"
                  outlined
                  hideDetails
                  dense
                  data-cy="select-product-category-language"
                  @input="updateLang"
                />
              </v-col>

              <!-- Spacer Element to push the Close Button to the right -->
              <v-spacer class="d-none d-md-flex" />
              <!-- Close Button Column, it will always stay to the right -->
            </v-row>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <component
              :is="component"
              v-bind="payload"
              @hide="hide"
              @security="getSecurity"
              @disabled="hasDisabled"
              @loading="isComponentLoading = $event"
              @sendData="requestPayload = $event"
              @inputChange="onInputChange"
            />
            <span v-if="removeMsgSecond">{{ removeMsgSecond }}</span>
          </v-card-text>

          <!-- Tabs Navigation -->
          <template v-if="showTabNavigations">
            <v-divider />
            <div
              v-if="showNextPrevious"
              class="modal-container"
            >
              <!-- PREVIOUS TAB -->
              <v-card-actions>
                <template>
                  <div
                    v-if="getTabs.length"
                    class="d-flex"
                  >
                    <v-btn
                      v-if="displayPrevious"
                      text
                      :ripple="false"
                      class="btn"
                      @click="previous"
                    >
                      <v-icon
                        class="icon"
                        large
                      >
                        arrow_left
                      </v-icon>
                      <span
                        class="arrow"
                      >
                        {{ $t('pagination.previous') }}
                      </span>
                    </v-btn>
                  </div>
                </template>
              </v-card-actions>
              <v-card-actions v-if="!isComponentLoading">
                <v-spacer />
                <!-- SAVE BUTTON -->
                <v-btn
                  v-if="showSaveBtn && (getTabs.length > 1 && getTabs.length !== activeTab + 1)"
                  text
                  :disabled="wasValid ? false : ((isForm && !valid)) || mustBeDisabled"
                  color="primary"
                  class="capitalize large"
                  data-cy="buttonSave"
                  @click="save"
                >
                  {{ $t('forms.labels.save') }}
                </v-btn>
                <!-- NEXT OR CONFIRM BUTTON -->
                <v-btn
                  v-if="showConfirmButton && displayNext"
                  :disabled="disableNextOrConfirm(valid)"
                  color="primary"
                  type="submit"
                  class="capitalize large"
                  data-cy="buttonNext"
                  @click.prevent="next"
                >
                  {{ $t('pagination.next') }}
                </v-btn>
                <v-btn
                  v-else
                  :disabled="disableNextOrConfirm(valid)"
                  color="primary"
                  type="submit"
                  class="capitalize large"
                  data-cy="buttonConfirm"
                  @click.prevent="confirm"
                  @dblclick="confirm"
                >
                  {{ $t(`forms.labels.${button}`) }}
                </v-btn>
              </v-card-actions>
            </div>
            <div
              v-else
              class="modal-container"
            >
              <v-card-actions>
                <template>
                  <div
                    v-if="getTabs.length"
                    class="d-flex"
                  >
                    <v-btn
                      v-if="activeTab"
                      text
                      :ripple="false"
                      class="btn"
                      @click="previous"
                    >
                      <v-icon
                        class="icon"
                        large
                      >
                        arrow_left
                      </v-icon>
                      <span
                        class="arrow"
                      >
                        {{ getTabs[activeTab - 1].name }}
                      </span>
                    </v-btn>
                    <v-btn
                      v-if="activeTab !== (getTabs.length - 1)"
                      text
                      :ripple="false"
                      class="btn"
                      style="margin-left: 20px;"
                      @click="next"
                    >
                      <span
                        class="arrow"
                      >
                        {{ getTabs[activeTab + 1].name }}
                      </span>
                      <v-icon
                        class="icon"
                        large
                      >
                        arrow_right
                      </v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-card-actions>
              <v-card-actions v-if="!isComponentLoading">
                <v-spacer />
                <v-btn
                  v-if="showConfirmButton"
                  :disabled="disableNextOrConfirm(valid)"
                  color="primary"
                  type="submit"
                  class="capitalize px-12 py-6 responsiveBtn"
                  data-cy="smallBtnConfirm"

                  @click.prevent="confirm"
                >
                  {{ $t(`forms.labels.${button}`) }}
                </v-btn>
              </v-card-actions>
            </div>
          </template>
        </v-card>
      </form>
    </v-dialog>
    <ConfirmModal
      :showConfirmBox="showConfirmBox"
      @hide="showConfirmBox = $event"
      @destroyModal="destroyModal"
    />
  </ValidationObserver>
</template>

<script>
import ConfirmModal from '@/components/shared/forms-controls/ConfirmModal.vue';
import { ValidationObserver } from 'vee-validate';
import modalMixin from '@/mixins/ModalMixin';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import { cloneDeep, isEmpty } from 'lodash';
import { modals } from '@/aq-data-cy.json';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';

export default {
    inject: ['$validator'],
    components: {
        VTextField,
        ValidationObserver,
        ConfirmModal,
        SlotScopeWatcher,
        VSelect,
    },
    mixins: [
        modalMixin,
    ],
    props: {
        params: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            buttonTitle: '', // name of the confirm button
            fullscreen: false,
            isForm: true,
            clean: false,
            isComponentLoading: false,
            payload: null, // object that has the data to display
            requestPayload: null, // object that has the data to submit
            showConfirmButton: true,
            mustBeDisabled: false,
            security: false,
            canDestroy: true,
            showNextPrevious: false,
            showTabNavigations: true,
            width: null,
            showLangs: false,
            lang: null,
            displayedLangs: [],
            previousValid: [false], // boolean array
            wasValid: false,
            oldValid: false,
            showSaveBtn: false,
            modalBreadcrumbs: [],
            showBreadcrumbs: false,
            dividerSelect: '',
            controlledValidation: false,
            resetData: () => null,
            disableNext: !this.isForm,
            validateFunctions: {},
            removeMsgSecond: '',
        };
    },
    computed: {
        ...mapGetters([
            'activeTab',
            'getTabs',
            'selectedLang',
            'allTeamLangs',
            'teamID',
        ]),
        button() {
            return (this.buttonTitle) ? this.buttonTitle : 'confirm';
        },
        displayPrevious() {
            return this.activeTab > 0;
        },
        displayNext() {
            return this.getTabs.length - 1 > this.activeTab;
        },
        dataCy() {
            return {
                modals,
            };
        },
    },
    watch: {
        selectedLang: {
            handler() {
                this.lang = this.selectedLang;
            },
        },
        params: {
            handler(values) {
                if (!isEmpty(values)) {
                    this.$store.dispatch('setTabs', []);
                    this.show(values);
                }
            },
            immediate: true,
        },
    },
    async created() {
        this.$root.$on('showModal', params => {
            this.showTabNavigations = params?.showTabNavigations ?? true;
            this.controlledValidation = params.controlledValidation;
            this.resetData = () => {
                if (params.resetData) params.resetData();
            };
            this.$store.dispatch('clearTabs');
            this.$store.dispatch('changeActiveTab', 0);
            this.show(params);
        });
        this.$root.$on('destroyModal', () => this.destroyModal());
        this.$root.$on('resetPreviousStep', () => {
            this.wasValid = true;
        });
    },
    methods: {
        ...mapMutations(['setSelectedLang', 'resetTeamState']),
        ...mapActions(['reloadTeam']),

        disableNextOrConfirm(valid) {
            if (this.controlledValidation) {
                return (this.isForm && this.disableNext) || this.mustBeDisabled;
            }
            return this.wasValid ? false : ((this.isForm && !valid)) || this.mustBeDisabled;
        },
        async confirm() {
            // Validate the inputs
            if (await this.isFormValid() || this.controlledValidation) {
                /* To disable button to avoid multiple click */
                this.mustBeDisabled = true;
                // Check if onConfirm is a function, otherwise just close it
                if (typeof this.onConfirm === 'function') {
                    try {
                        await this.onConfirm(this.requestPayload);
                    } catch (error) {
                        // if onConfirm failed we reset the confirm button
                        this.mustBeDisabled = false;
                        return;
                    }
                }
                this.requestPayload = null;

                // in other use case we want to destroy the component manually.
                if (this.canDestroy) {
                    this.destroyModal();
                }
                // reset modal header
                this.$store.dispatch('setTabs', []);
            }
        },
        async save() {
            // Validate the inputs
            if (await this.isFormValid()) {
                /* To disable button to avoid multiple click */
                this.mustBeDisabled = true;
                // Check if onConfirm is a function, otherwise just close it
                if (typeof this.onConfirm === 'function') {
                    try {
                        await this.onConfirm(this.requestPayload);
                    } catch (error) {
                        // if onConfirm failed we reset the confirm button
                        this.mustBeDisabled = false;
                        return;
                    }
                }
                this.mustBeDisabled = false;
                this.requestPayload = null;
            }
        },
        async isFormValid(silent = false) {
            return this.$refs.modalObserver.validate({ silent });
        },
        getSecurity(val) {
            this.security = true;
            if (val === this.$t('forms.labels.security')) {
                this.mustBeDisabled = false;
            } else {
                this.mustBeDisabled = true;
            }
        },
        hasDisabled(val) {
            this.mustBeDisabled = val;
        },
        async show(params) {
            if (params.payload) {
                const { netWorkName, node, productInfo, showBreadcrumbs, removeMsgSecond } = params.payload;
                this.removeMsgSecond = removeMsgSecond ?? '';
                if (netWorkName) {
                    this.modalBreadcrumbs = [{ text: netWorkName }, { text: node?.name }];
                    this.showBreadcrumbs = showBreadcrumbs || false;
                    this.dividerSelect = '->';
                } else if (productInfo) {
                    this.modalBreadcrumbs = [{ text: productInfo[0].code }, { text: productInfo[0].name }];
                    this.showBreadcrumbs = showBreadcrumbs || false;
                    this.dividerSelect = '-';
                } else {
                    this.showBreadcrumbs = false;
                }
            } else {
                this.showBreadcrumbs = false;
            }
            // to set the langs bar
            if (params.showLangs) {
                await this.reloadTeam(this.teamID);
                this.prepareLangsName();
            }
            this.visible = true; // show the modal
            // required
            this.title = params.title;
            this.meterName = params.meterName;
            this.component = params.component;

            // optional
            this.width = params.width ? params.width : 1126;
            this.buttonTitle = params.buttonTitle; // default is Confirm
            this.fullscreen = params.fullscreen === true;
            this.onConfirm = params.onConfirm;
            this.payload = params.payload;
            this.canDestroy = params.destroy === undefined || params.destroy;
            this.showNextPrevious = params.showNextPrevious;
            this.isForm = Object.prototype.hasOwnProperty.call(params, 'isForm') ? params.isForm : true;
            this.showConfirmButton = params.showConfirmButton !== undefined ? params.showConfirmButton : true;
            this.mustBeDisabled = params.mustBeDisabled;
            this.width = params.width ? params.width : 1126;
            this.showLangs = !!params?.showLangs;
            this.lang = this.selectedLang;
            this.showSaveBtn = params.showSaveBtn;
        },
        updateLang(val) {
            this.setSelectedLang(val);
        },
        async next() {
            const targetTab = this.activeTab + 1;
            const nextValidateFunction = this.validateFunctions[targetTab];
            this.$store.dispatch('changeActiveTab', targetTab);
            const valid = await this.isFormValid();
            if (this.controlledValidation) {
                this.disableNext = nextValidateFunction ? !await nextValidateFunction({ silent: true }) : this.disableNext;
            } else if (!valid && this.previousValid[targetTab]) {
                this.wasValid = true;
            } else {
                // this.activeTab is not zero indexed
                this.previousValid[this.activeTab - 1] = valid;
                if (this.wasValid) {
                    this.wasValid = false;
                }
            }
        },
        async previous() {
            const targetTab = this.activeTab - 1;
            const previousValidateFunction = this.validateFunctions[targetTab];
            this.$store.dispatch('changeActiveTab', targetTab);
            const valid = await this.isFormValid();
            if (this.controlledValidation) {
                this.disableNext = previousValidateFunction ? !await previousValidateFunction({ silent: true }) : true;
            } else if (!valid && this.previousValid[targetTab]) {
                // not valid but the previous step was already validated
                this.wasValid = true;
            }
        },
        async onInputChange(validateFn) {
            if (this.controlledValidation && validateFn) {
                this.validateFunctions[this.activeTab] = validateFn;
                this.disableNext = !await validateFn({ silent: true });
            } else {
                const valid = await this.isFormValid(true);
                const nextStep = this.activeTab + 1;
                if (!valid) {
                    /* every time a input field get updated,
                    and the form is not valid anymore,
                    we ignore the "wasValid" */

                    //  if next step does not exist or if false
                    if (!this.previousValid[nextStep] && this.wasValid) {
                        return;
                    }
                    this.wasValid = false;
                }
            }
        },
        prepareLangsName() {
            const temp = cloneDeep(this.allTeamLangs);
            temp.forEach(lang => {
                // eslint-disable-next-line no-param-reassign
                lang.name = this.$t(`langs.${lang.key}`);
            });
            this.displayedLangs = temp;
        },
    },
};
</script>
<style lang="scss" scoped>
.modal-container {
  display: flex;
  justify-content: space-between;
  .btn {
    text-transform: inherit;
    letter-spacing: normal;
    padding: 0;
    &::before {
      display: none;
    }
  }
}
.arrow {
  color:#26384b;
  font-size: 16px;
}
.capitalize{
  text-transform: capitalize !important;
  font-size: 16px !important;
}
.large {
  min-width: 150px !important;
}
.langBox {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
* {
  word-break: break-word;
}
.mention {
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  transform: translateY(-16px);
  font-size: 1rem;
  font-weight: 400;
  font-style: italic;
}
.close{
  text-align: right;
  margin-right: -20px;
}

@media screen and (max-width: 600px) {
  .blocLang {
    display: flex;
    flex-direction: column;
    justify-content: center !important
  }

  .title {
    text-align: center;
  }
  .mention {
    justify-content: center
  }
  .responsiveBtn {
    width: 100px !important;
  }
}

.breadCrumbStyle{
   display: flex;
}

.titleStyle{
    display: flex;
    flex: none;
    flex-wrap: wrap;
    max-width: 75%;
    column-gap: 1rem;

    ::v-deep .v-breadcrumbs{
        padding: 0 !important;
    }
}


</style>
